import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FormStepper from "../../../components/common/FormStepper";
import FormRenderer from "../../../components/common/FormRenderer";
import { ChevronLeft, ChevronRight, AlertTriangle, Save, X, ArrowLeft } from "lucide-react";
import formatError from "../../../utils/formatError";
import { useVoicePage } from "../../../hooks/useVoicePage";
import VoicePageMic from "../../../components/common/VoicePageMic";


// Normalise a token for case-insensitive comparison.
const _n = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ").trim();

// Map a spoken field name to the schema's machine field name.
// Considers:
//   - exact match on f.name (e.g. "first_name", "nationality")
//   - exact + lower-case match on f.displayName ("First Name")
//   - common aliases ("dob" → "date_of_birth", "phone" → "phone_number")
function findField(allFields, spokenName) {
  if (!spokenName) return null;
  const target = _n(spokenName);
  if (!target) return null;
  // exact name / display name match (case-insensitive, ignore punctuation)
  for (const f of allFields) {
    if (_n(f.name) === target) return f;
    if (_n(f.displayName) === target) return f;
    // also try field.name with underscores stripped
    if (_n(f.name.replace(/_/g, " ")) === target) return f;
  }
  // Substring fallback — useful when the operator says "first" and there's
  // exactly one matching field. If multiple match, refuse (ambiguous).
  const matches = allFields.filter((f) => {
    const nm = _n(f.name).replace(/_/g, " ");
    const dn = _n(f.displayName);
    return nm.includes(target) || target.includes(nm) || dn.includes(target);
  });
  if (matches.length === 1) return matches[0];
  return null;
}

// Coerce a spoken value to whatever the field expects. Booleans handle
// yes/no/true/false; selects look up the matching option label; the
// rest pass through as strings.
function coerceValue(field, spoken) {
  const s = (spoken || "").trim();
  if (field.type === "select" || field.type === "multiselect") {
    const opts = field.options || [];
    const target = _n(s);
    const hit = opts.find((o) => _n(o.label) === target || _n(o.value) === target)
             || opts.find((o) => _n(o.label).includes(target) || target.includes(_n(o.label)));
    return hit ? hit.value : null;
  }
  if (field.type === "checkbox" || field.type === "switch") {
    if (/^(yes|true|on|enabled?|tick(ed)?|checked)$/i.test(s)) return true;
    if (/^(no|false|off|disabled?|unchecked|uncheck)$/i.test(s)) return false;
    return null;
  }
  if (field.type === "text" && field.inputType === "number") {
    const num = parseFloat(s);
    return isNaN(num) ? null : num;
  }
  // Free text — return as-is. Numbers spoken as words ("ten") could be
  // normalised later if needed; today's forms don't require it.
  return s;
}

const DRAFT_VERSION = 1;
const DRAFT_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function draftKey(title) {
  return `zcd_add_draft__${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}__v${DRAFT_VERSION}`;
}

function loadDraft(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    if (Date.now() - (obj.savedAt || 0) > DRAFT_TTL_MS) return null;
    return obj;
  } catch {
    return null;
  }
}

function saveDraft(key, payload) {
  try {
    localStorage.setItem(key, JSON.stringify({ ...payload, savedAt: Date.now() }));
  } catch {
    /* quota exceeded — drafts are best-effort */
  }
}

function clearDraft(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

// Step-level validation: count required fields and how many are filled.
// Reads `required: true` on each FormRenderer field def. Considers
// multiselect arrays "filled" only when non-empty.
function validateStep(step, values) {
  const required = (step.fields || []).filter((f) => f.required);
  const missing = required.filter((f) => {
    const v = values[f.name];
    if (v === undefined || v === null || v === "") return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
  });
  return { required, missing, ok: missing.length === 0 };
}

export default function AddTemplate({
  title,
  schema,
  createFn,
  redirectPath,
  // Optional: called with current `values` before save. Resolve to an
  // array of {id, label} possible duplicates. Triggers the "did you mean
  // …?" modal before the create call so users don't generate noise.
  duplicateCheckFn,
}) {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [dupCandidates, setDupCandidates] = useState(null); // null | array
  const navigate = useNavigate();

  const key = useMemo(() => draftKey(title), [title]);

  // Draft restore on mount. We don't autofill silently — show a banner
  // and let the user explicitly resume or discard so they're never
  // confused by mystery values.
  const [draftAvailable, setDraftAvailable] = useState(() => {
    const d = loadDraft(key);
    return d && d.values && Object.keys(d.values).length > 0 ? d : null;
  });

  // Auto-save draft on every change (debounced).
  useEffect(() => {
    if (Object.keys(values).length === 0) return undefined;
    const t = setTimeout(() => saveDraft(key, { values, step }), 400);
    return () => clearTimeout(t);
  }, [values, step, key]);

  // Step navigation guards.
  const currentValidation = useMemo(
    () => validateStep(schema.steps[step], values),
    [schema.steps, step, values],
  );

  const next = useCallback(() => {
    if (!currentValidation.ok) {
      setShowStepErrors(true);
      return;
    }
    setShowStepErrors(false);
    setStep((s) => Math.min(s + 1, schema.steps.length - 1));
  }, [currentValidation.ok, schema.steps.length]);

  const prev = () => {
    setShowStepErrors(false);
    setStep((s) => Math.max(s - 1, 0));
  };

  // Sanitise the form payload before POSTing. The biggest gotcha is
  // empty-string values from FormRenderer (every untouched text input
  // defaults to ""): for fields like date_of_birth this trips DRF
  // DateField with an obscure "Date has wrong format" error. We:
  //   * Drop empty-string values for date / number / select / multiselect
  //     fields whose schema doesn't mark them required — DRF then
  //     treats the missing key as "not supplied" and uses the model
  //     default / null.
  //   * Keep empty strings for free-text fields (CharField on the model
  //     with blank=True, default="" accepts ""), so the operator can
  //     intentionally clear a value during Edit.
  const cleanPayload = useCallback(() => {
    const out = { ...values };
    const stepFields = (schema.steps || []).flatMap((s) => s.fields || []);
    for (const f of stepFields) {
      const v = out[f.name];
      if (v === "" || v == null) {
        const isDate = f.type === "text" && f.inputType === "date";
        const isNum  = f.type === "text" && f.inputType === "number";
        const isPicker = f.type === "select" || f.type === "multiselect";
        if (isDate || isNum || isPicker) delete out[f.name];
      }
    }
    return out;
  }, [values, schema]);

  const realSave = useCallback(async () => {
    setError(null);
    setSaving(true);
    try {
      await createFn(cleanPayload());
      clearDraft(key);
      navigate(redirectPath);
    } catch (e) {
      setError(formatError(e, `Failed to create ${title.toLowerCase()}.`));
      setSaving(false);
    }
  }, [createFn, cleanPayload, key, navigate, redirectPath, title]);

  const save = useCallback(async () => {
    if (!currentValidation.ok) {
      setShowStepErrors(true);
      return;
    }
    // Run optional duplicate check; show modal if candidates returned.
    if (duplicateCheckFn) {
      try {
        const candidates = await duplicateCheckFn(values);
        if (Array.isArray(candidates) && candidates.length > 0) {
          setDupCandidates(candidates);
          return;
        }
      } catch {
        // Duplicate check failures should NOT block a real save — fall
        // through and let the server enforce uniqueness.
      }
    }
    realSave();
  }, [currentValidation.ok, duplicateCheckFn, values, realSave]);

  // Enter-to-next on form-step inputs. We want this only inside the form
  // itself, and only when the focused element is a plain input/select —
  // textareas should still accept Enter to insert newlines.
  const onFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const tag = (e.target?.tagName || "").toLowerCase();
    if (tag === "textarea") return;
    // Don't intercept inside MultiSelect / SearchableSelect dropdowns
    // that may be reading Enter for selection.
    if (e.target?.dataset?.consumeEnter === "true") return;
    e.preventDefault();
    if (step < schema.steps.length - 1) next();
    else save();
  };

  const onStepClick = (idx) => {
    if (idx < step) {
      setStep(idx);
      setShowStepErrors(false);
    }
  };

  /* =========================
     Voice — generic field-fill across every Add form

     Pattern: "<field> <value>". Field name is matched against the
     schema's displayName / name (with common aliases). Value is
     coerced to whatever the field expects (select option / boolean
     / number / text).

     Examples (Player Add):
       "first name Rohit"
       "last name Sharma"
       "nationality India"
       "date of birth 1987 04 30"
       "batting hand right"
     Examples (Match Add):
       "match type T20"
       "venue Wankhede"
       "next"
       "save"

     Compound: comma-separate to set multiple fields at once.
       "first name Rohit, last name Sharma, nationality India"

     Navigation: "next" / "previous" / "save" / "go to step 2".
     Save and Next stay manual via button — voice can trigger them
     but auto-advance is OFF per founder ask.
  ========================= */
  const allFields = useMemo(() => (schema.steps || []).flatMap((s) => s.fields || []), [schema]);

  const formVoiceParser = useCallback((raw) => {
    const t = (raw || "").toLowerCase().replace(/[.,!?]+$/g, "").trim();
    if (!t) return null;

    // Navigation commands first — match against the WHOLE utterance so
    // "save" isn't mistakenly parsed as a field value.
    if (/^(go to )?(next|next step|continue)\s*$/.test(t)) {
      // Defer to next-tick so any state updates from prior voice
      // commands flush before the gate check.
      setTimeout(() => {
        document.getElementById("template-next-btn")?.click();
      }, 0);
      return "→ next step";
    }
    if (/^(go )?back|previous(\s+step)?|prev\s*$/.test(t)) {
      setTimeout(() => {
        document.getElementById("template-prev-btn")?.click();
      }, 0);
      return "← previous step";
    }
    if (/^save( it)?\s*$/.test(t) || /^submit\s*$/.test(t) || /^(create|finish)\s*$/.test(t)) {
      setTimeout(() => {
        document.getElementById("template-save-btn")?.click();
      }, 0);
      return "save";
    }
    const stepMatch = t.match(/^(?:go to )?step\s+(\d+|one|two|three|four|five)$/);
    if (stepMatch) {
      const wordMap = { one: 1, two: 2, three: 3, four: 4, five: 5 };
      const n = parseInt(stepMatch[1], 10) || wordMap[stepMatch[1]] || null;
      if (n && n >= 1 && n <= schema.steps.length) {
        setStep(n - 1);
        return `→ step ${n}`;
      }
    }

    // Multi-field: split on " , " or " ; " and parse each segment as
    // its own "field value" pair. Falls back to single-segment.
    const segments = t.split(/\s*(?:,|;)\s*/).map((s) => s.trim()).filter(Boolean);
    const updates = {};
    const applied = [];
    const misses = [];

    for (const seg of segments) {
      // Split each segment on the first space pair — field name can
      // be multi-word, so try matching progressively shorter prefixes.
      const tokens = seg.split(/\s+/);
      if (tokens.length < 2) {
        misses.push(seg);
        continue;
      }
      // Try the longest field-name prefix first; back off until a
      // matching field is found (or none).
      let field = null;
      let valueText = "";
      for (let k = Math.min(4, tokens.length - 1); k >= 1; k--) {
        const prefix = tokens.slice(0, k).join(" ");
        const f = findField(allFields, prefix);
        if (f) {
          field = f;
          valueText = tokens.slice(k).join(" ");
          break;
        }
      }
      if (!field) {
        misses.push(seg);
        continue;
      }
      const v = coerceValue(field, valueText);
      if (v === null) {
        misses.push(`${field.displayName || field.name}=?`);
        continue;
      }
      updates[field.name] = v;
      applied.push(`${field.displayName || field.name}=${v}`);
    }

    if (Object.keys(updates).length === 0) {
      // ── AI FALLBACK ────────────────────────────────────────────
      // Operator spoke something the field-prefix regex couldn't
      // resolve ("T20 RCB vs MI at Wankhede tomorrow 7pm"). Send
      // the full transcript + current-step schema to Claude Haiku
      // via /api/scoring/ai/form-parse/; the response is a
      // {field_name: value} dict matching the schema.
      // Founder ask 2026-05-26: "Prioritize match creation — team
      // is testing live."
      const stepFields = (schema.steps[step] || {}).fields || [];
      const schemaPayload = stepFields.map((f) => ({
        name: f.name,
        type: f.type || "text",
        inputType: f.inputType || "",
        displayName: f.displayName || f.name,
        options: (f.options || []).map((o) => ({
          label: o.label || String(o.value),
          value: o.value,
        })),
      }));
      // Fire-and-forget: the parser callback is sync but we kick off
      // the AI call asynchronously and apply on resolve. Operator
      // sees a "🤖 thinking…" placeholder in the recent strip.
      (async () => {
        try {
          const api = (await import("../../../api/axios")).default;
          const { data } = await api.post("/api/scoring/ai/form-parse/", {
            transcript: raw,
            form_kind: (title || "form").toLowerCase().replace(/[^a-z]/g, ""),
            fields: schemaPayload,
          });
          const ups = data?.updates || {};
          if (!ups || Object.keys(ups).length === 0) return;
          setValues((prev) => ({ ...prev, ...ups }));
          if (showStepErrors) setShowStepErrors(false);
        } catch (e) {
          console.warn("voice-form AI fallback failed:", e?.response?.data || e);
        }
      })();
      return misses.length
        ? `🤖 thinking… (couldn't match: ${misses.slice(0, 2).join(", ")})`
        : "🤖 thinking…";
    }
    setValues((prev) => ({ ...prev, ...updates }));
    if (showStepErrors) setShowStepErrors(false);
    return applied.join(" · ");
  }, [allFields, schema.steps, step, showStepErrors, title]);

  const voice = useVoicePage({ parser: formVoiceParser });

  // Example phrases to show in the help modal — derived from the
  // current step so the prompts are always relevant.
  const voiceExamples = useMemo(() => {
    const ex = [];
    const fields = (schema.steps[step] || {}).fields || [];
    for (const f of fields.slice(0, 4)) {
      const label = (f.displayName || f.name).toLowerCase();
      if (f.type === "select" && f.options?.length) {
        ex.push(`${label} ${f.options[0].label || f.options[0].value}`);
      } else if (f.type === "checkbox" || f.type === "switch") {
        ex.push(`${label} yes`);
      } else if (f.inputType === "number") {
        ex.push(`${label} 5`);
      } else {
        ex.push(`${label} value`);
      }
    }
    ex.push("next");
    if (step === schema.steps.length - 1) ex.push("save");
    return ex;
  }, [schema.steps, step]);

  // Resume / discard draft.
  const resumeDraft = () => {
    if (!draftAvailable) return;
    setValues(draftAvailable.values || {});
    setStep(Math.min(draftAvailable.step || 0, schema.steps.length - 1));
    setDraftAvailable(null);
  };
  const discardDraft = () => {
    clearDraft(key);
    setDraftAvailable(null);
  };

  // 2026-06-08 — explicit exit affordance. Founder navigation audit:
  // the wizard had no Cancel / Back control, so an operator who tapped
  // Add by mistake was stranded (browser back was the only out, and on
  // phones the global nav is hidden behind the hamburger). cancelEdit
  // confirms only when there's draft content so an empty-wizard back-out
  // doesn't nag.
  const cancelEdit = () => {
    const isDirty = Object.keys(values).some(
      (k) => values[k] !== undefined && values[k] !== "" && values[k] !== null,
    );
    if (isDirty && !window.confirm(
      "Leave without saving? Your draft is auto-saved and you can resume next time.",
    )) return;
    navigate(redirectPath || "/cricket/matches");
  };

  return (
    <div>
      <button
        onClick={cancelEdit}
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-3"
        aria-label="Back to list"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="opacity-50 text-xl">{title}</h2>
        <VoicePageMic
          voice={voice}
          helpTitle={`${title} — voice commands`}
          examples={voiceExamples}
          helpBody={
            <>
              Format: <em>"field-name value"</em> — say the field name
              (as shown on the form) followed by the value. Combine
              multiple with commas: <em>"first name Rohit, last name
              Sharma"</em>. Say <em>next</em>, <em>previous</em>,
              <em> step 2</em>, <em>save</em> to navigate. Save / Next
              buttons remain manual so you can verify before
              committing.
            </>
          }
        />
      </div>

      {draftAvailable && (
        <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm flex flex-wrap items-center gap-2">
          <Save className="w-4 h-4 text-amber-300" />
          <span className="text-amber-100">
            You have an unfinished draft from {new Date(draftAvailable.savedAt).toLocaleString()}.
          </span>
          <div className="flex-1" />
          <button
            onClick={resumeDraft}
            className="text-[11px] uppercase tracking-wider px-3 py-1 rounded bg-amber-500 text-black hover:bg-amber-400 font-semibold"
          >
            Resume
          </button>
          <button
            onClick={discardDraft}
            className="text-[11px] uppercase tracking-wider px-3 py-1 rounded bg-bg-secondary text-text-secondary border border-border-default hover:text-text-primary"
          >
            Start fresh
          </button>
        </div>
      )}

      <div className="pb-16 bg-bg-primary mt-8 rounded-lg">
        <FormStepper
          steps={schema.steps}
          activeStep={step}
          onStepClick={onStepClick}
        />
        <div className="flex flex-col justify-between min-h-96" onKeyDown={onFormKeyDown}>
          <FormRenderer
            step={schema.steps[step]}
            values={values}
            onChange={(k, v) => {
              setValues((prev) => ({ ...prev, [k]: v }));
              if (showStepErrors) setShowStepErrors(false);
            }}
          />

          {showStepErrors && currentValidation.missing.length > 0 && (
            <div
              role="alert"
              className="mx-8 mt-4 px-4 py-3 rounded-md bg-rose-500/10 border border-rose-500/40 text-rose-200 text-sm"
            >
              <div className="font-semibold mb-1 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Fill these required fields to continue:
              </div>
              <ul className="list-disc list-inside text-[12px]">
                {currentValidation.missing.map((f) => (
                  <li key={f.name}>{f.displayName || f.name}</li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="mx-8 mt-4 px-4 py-3 rounded bg-red-500/10 border border-red-500/40 text-red-300 whitespace-pre-line text-sm"
            >
              {error}
            </div>
          )}

          {/* Form footer — stacks on phone so Prev/Next/Save don't get
              pushed off-screen by the Enter-hint. py-3 on phone meets
              44px WCAG touch target; py-2 on sm+ keeps the old density. */}
          <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center sm:justify-end px-3 sm:px-8">
            <span className="hidden sm:inline text-[11px] text-text-secondary sm:mr-auto">
              Press <kbd className="px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default text-[10px] font-mono">Enter</kbd> to advance
              {Object.keys(values).length > 0 && <span> · Draft auto-saves</span>}
            </span>
            <div className="flex gap-2 sm:gap-4">
              <button
                onClick={cancelEdit}
                className="flex-1 sm:flex-initial bg-bg-secondary text-text-secondary border border-border-default hover:text-text-primary px-4 sm:px-8 py-3 sm:py-2 rounded"
              >
                Cancel
              </button>
              {step > 0 && (
                <button
                  id="template-prev-btn"
                  onClick={prev}
                  className="flex-1 sm:flex-initial bg-input-secondary px-4 sm:px-8 py-3 sm:py-2 rounded flex items-center justify-center gap-2 sm:gap-4"
                >
                  <ChevronLeft />
                  Previous
                </button>
              )}
              {step < schema.steps.length - 1 && (
                <button
                  id="template-next-btn"
                  onClick={next}
                  className="flex-1 sm:flex-initial bg-input-secondary px-4 sm:px-8 py-3 sm:py-2 rounded flex items-center justify-center gap-2 sm:gap-4"
                  title={currentValidation.ok ? "" : `Fill ${currentValidation.missing.length} required field(s) to continue`}
                >
                  Next
                  <ChevronRight />
                </button>
              )}
              {step === schema.steps.length - 1 && (
                <button
                  id="template-save-btn"
                  disabled={saving}
                  onClick={save}
                  className="flex-1 sm:flex-initial bg-button-primary px-4 sm:px-8 py-3 sm:py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {dupCandidates && (
        <DuplicateDialog
          candidates={dupCandidates}
          onCancel={() => setDupCandidates(null)}
          onContinue={() => {
            setDupCandidates(null);
            realSave();
          }}
        />
      )}
    </div>
  );
}

function DuplicateDialog({ candidates, onCancel, onContinue }) {
  // 2026-06-08 — Esc / backdrop / hardware-back. Inline so we don't
  // pull useDialogDismiss into a hot-path component without need —
  // this dialog only mounts when a duplicate is found.
  const panelRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Possible duplicate"
        className="bg-bg-primary border border-border-default rounded-xl shadow-2xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-300" />
            Possible duplicate{candidates.length === 1 ? "" : "s"}
          </h3>
          <button onClick={onCancel} className="text-text-secondary hover:text-text-primary"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-text-secondary mb-3">
          We found {candidates.length} entr{candidates.length === 1 ? "y" : "ies"} that look similar.
          Open one to use it, or continue to create a new entry anyway.
        </p>
        <ul className="space-y-1.5 mb-4 max-h-60 overflow-y-auto">
          {candidates.map((c) => (
            <li key={c.id}>
              {c.url ? (
                <a
                  href={c.url}
                  className="block px-3 py-2 rounded bg-bg-secondary/50 hover:bg-bg-secondary text-sm text-text-primary"
                >
                  {c.label} <span className="text-text-secondary text-[11px]">— open →</span>
                </a>
              ) : (
                <div className="px-3 py-2 rounded bg-bg-secondary/50 text-sm text-text-primary">{c.label}</div>
              )}
            </li>
          ))}
        </ul>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-3 py-1.5 rounded text-sm bg-bg-secondary text-text-secondary hover:text-text-primary">
            Cancel
          </button>
          <button onClick={onContinue} className="px-3 py-1.5 rounded text-sm bg-button-primary text-white">
            Create anyway
          </button>
        </div>
      </div>
    </div>
  );
}
